// Generat automat din README.md
import React, { useState } from 'react';
import { Truck, Package, MapPin, Clock, Navigation, BarChart3, AlertCircle, CheckCircle, RefreshCw, Search, Eye, Plus, Download, DollarSign } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const IntegrareLogistica = () => {
  const [selectedCarrier, setSelectedCarrier] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showAddShipmentModal, setShowAddShipmentModal] = useState(false);

  // Date integrate de logistică
  const logisticsData = {
    shipments: [
      {
        id: 'SHP-001',
        orderId: 'ORDER-2024-001',
        customer: 'Tech Solutions SRL',
        carrier: 'FAN Courier',
        trackingNumber: 'FAN123456789',
        status: 'În tranzit',
        origin: 'București Depozit Central',
        destination: 'Cluj-Napoca, Str. Memorandumului 15',
        estimatedDelivery: '2024-03-18 16:00',
        actualDelivery: null,
        weight: 12.5,
        dimensions: '40x30x15 cm',
        value: 2500,
        lastUpdate: '2024-03-16 14:30',
        notes: 'Fragil - Manevrare cu grijă'
      },
      {
        id: 'SHP-002',
        orderId: 'ORDER-2024-002',
        customer: 'Marketing Pro SA',
        carrier: 'Cargus',
        trackingNumber: 'CRG987654321',
        status: 'Livrat',
        origin: 'București Depozit Central',
        destination: 'Timișoara, Bd. Liviu Rebreanu 32',
        estimatedDelivery: '2024-03-15 12:00',
        actualDelivery: '2024-03-15 11:45',
        weight: 8.2,
        dimensions: '35x25x20 cm',
        value: 1800,
        lastUpdate: '2024-03-15 11:45',
        notes: 'Livrat cu succes'
      },
      {
        id: 'SHP-003',
        orderId: 'ORDER-2024-003',
        customer: 'Startup Innovation',
        carrier: 'DPD',
        trackingNumber: 'DPD456789123',
        status: 'Procesare',
        origin: 'București Depozit Central',
        destination: 'Iași, Str. Carol I nr. 8',
        estimatedDelivery: '2024-03-20 14:00',
        actualDelivery: null,
        weight: 5.8,
        dimensions: '25x20x10 cm',
        value: 950,
        lastUpdate: '2024-03-16 09:15',
        notes: 'În pregătire pentru expediere'
      },
      {
        id: 'SHP-004',
        orderId: 'ORDER-2024-004',
        customer: 'Government Agency',
        carrier: 'UPS',
        trackingNumber: 'UPS789123456',
        status: 'Întârziere',
        origin: 'București Depozit Central',
        destination: 'Constanța, Str. Portului 42',
        estimatedDelivery: '2024-03-17 10:00',
        actualDelivery: null,
        weight: 18.7,
        dimensions: '60x40x25 cm',
        value: 4500,
        lastUpdate: '2024-03-16 16:22',
        notes: 'Întârziere din cauza condițiilor meteo'
      }
    ],

    carriers: [
      {
        id: 'fan-courier',
        name: 'FAN Courier',
        status: 'Activ',
        reliability: 94.5,
        avgDeliveryTime: '24h',
        coverage: 'Național',
        costPerKg: 8.50,
        activeShipments: 15,
        totalShipments: 142
      },
      {
        id: 'cargus',
        name: 'Cargus',
        status: 'Activ',
        reliability: 92.1,
        avgDeliveryTime: '36h',
        coverage: 'Național + Internațional',
        costPerKg: 7.80,
        activeShipments: 8,
        totalShipments: 89
      },
      {
        id: 'dpd',
        name: 'DPD',
        status: 'Activ',
        reliability: 96.8,
        avgDeliveryTime: '48h',
        coverage: 'Național + Internațional',
        costPerKg: 9.20,
        activeShipments: 12,
        totalShipments: 78
      },
      {
        id: 'ups',
        name: 'UPS',
        status: 'Activ',
        reliability: 89.3,
        avgDeliveryTime: '72h',
        coverage: 'Internațional',
        costPerKg: 15.40,
        activeShipments: 3,
        totalShipments: 23
      }
    ]
  };

  // Statistici generale
  const logisticsStats = {
    totalShipments: logisticsData.shipments.length,
    inTransit: logisticsData.shipments.filter(s => s.status === 'În tranzit').length,
    delivered: logisticsData.shipments.filter(s => s.status === 'Livrat').length,
    delayed: logisticsData.shipments.filter(s => s.status === 'Întârziere').length,
    avgDeliveryTime: '32h',
    totalValue: logisticsData.shipments.reduce((acc, ship) => acc + ship.value, 0),
    onTimeRate: 89.2
  };

  const carriers = ['all', 'FAN Courier', 'Cargus', 'DPD', 'UPS'];
  const statuses = ['all', 'Procesare', 'În tranzit', 'Livrat', 'Întârziere'];

  const getStatusColor = (status) => {
    const colors = {
      'Procesare': 'info',
      'În tranzit': 'warning',
      'Livrat': 'success',
      'Întârziere': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Procesare': Clock,
      'În tranzit': Truck,
      'Livrat': CheckCircle,
      'Întârziere': AlertCircle
    };
    return icons[status] || Package;
  };

  const handleShipmentClick = (shipment) => {
    setSelectedShipment(shipment);
    setShowShipmentModal(true);
  };

  const shipmentsColumns = [
    { 
      header: 'Expediere', 
      key: 'id',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Comanda: {row.orderId}</p>
        </div>
      )
    },
    { header: 'Client', key: 'customer' },
    { header: 'Curier', key: 'carrier' },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => {
        const Icon = getStatusIcon(value);
        return (
          <div className="flex items-center space-x-2">
            <Icon className="w-4 h-4" />
            <Badge variant={getStatusColor(value)}>{value}</Badge>
          </div>
        );
      }
    },
    { 
      header: 'Destinație', 
      key: 'destination',
      render: (value) => (
        <div className="max-w-48 truncate" title={value}>
          {value}
        </div>
      )
    },
    { 
      header: 'Livrare Estimată', 
      key: 'estimatedDelivery',
      render: (value) => new Date(value).toLocaleDateString('ro-RO') + ' ' + new Date(value).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    },
    { 
      header: 'Valoare', 
      key: 'value',
      render: (value) => `€${value.toLocaleString()}`
    }
  ];

  const carriersColumns = [
    { 
      header: 'Curier', 
      key: 'name',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
            {value.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{row.coverage}</p>
          </div>
        </div>
      )
    },
    { 
      header: 'Fiabilitate', 
      key: 'reliability',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${value >= 95 ? 'bg-green-500' : value >= 90 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { header: 'Timp Mediu', key: 'avgDeliveryTime' },
    { 
      header: 'Cost/kg', 
      key: 'costPerKg',
      render: (value) => `€${value}`
    },
    { header: 'Active', key: 'activeShipments' },
    { header: 'Total', key: 'totalShipments' }
  ];

  const filteredShipments = logisticsData.shipments.filter(shipment => {
    const matchesCarrier = selectedCarrier === 'all' || shipment.carrier === selectedCarrier;
    const matchesStatus = selectedStatus === 'all' || shipment.status === selectedStatus;
    const matchesSearch = shipment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shipment.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shipment.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCarrier && matchesStatus && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Integrare Logistică</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sincronizare cu sistemele de transport și tracking livrări în timp real
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAddShipmentModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            <span>Expediere Nouă</span>
          </button>

          <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Statistici generale */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <Package className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.totalShipments}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Expedieri</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Truck className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.inTransit}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">În Tranzit</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.delivered}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Livrate</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.delayed}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Întârziate</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.avgDeliveryTime}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Timp Mediu</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{logisticsStats.onTimeRate}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">La Timp</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <DollarSign className="w-8 h-8 text-pink-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">€{(logisticsStats.totalValue / 1000).toFixed(0)}K</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Valoare Total</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtre și căutare */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Caută după ID, client sau tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <select
            value={selectedCarrier}
            onChange={(e) => setSelectedCarrier(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            {carriers.map(carrier => (
              <option key={carrier} value={carrier}>
                {carrier === 'all' ? 'Toți curierii' : carrier}
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'Toate statusurile' : status}
              </option>
            ))}
          </select>

          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <RefreshCw className="w-4 h-4" />
            <span>Actualizează</span>
          </button>
        </div>
      </Card>

      {/* Tabel expedieri */}
      <Card title="Expedieri Active" icon={Package}>
        <Table
          columns={shipmentsColumns}
          data={filteredShipments}
          onRowClick={(row) => handleShipmentClick(row)}
        />
      </Card>

      {/* Curieri */}
      <Card title="Performanța Curierilor" icon={Truck}>
        <Table columns={carriersColumns} data={logisticsData.carriers} />
      </Card>

      {/* Modal detalii expediere */}
      {showShipmentModal && selectedShipment && (
        <Modal
          isOpen={showShipmentModal}
          onClose={() => setShowShipmentModal(false)}
          title={`Detalii Expediere - ${selectedShipment.id}`}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedShipment.id}</h2>
                <p className="text-gray-600 dark:text-gray-400">Comanda: {selectedShipment.orderId}</p>
              </div>
              <div className="flex items-center space-x-2">
                {(() => {
                  const Icon = getStatusIcon(selectedShipment.status);
                  return <Icon className="w-5 h-5" />;
                })()}
                <Badge variant={getStatusColor(selectedShipment.status)} className="text-lg p-2">
                  {selectedShipment.status}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Informații Transport</h3>
                
                <div className="flex items-center space-x-3">
                  <Truck className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Curier</p>
                    <p className="font-medium">{selectedShipment.carrier}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Package className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Cod tracking</p>
                    <p className="font-medium font-mono">{selectedShipment.trackingNumber}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Origen</p>
                    <p className="font-medium">{selectedShipment.origin}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Navigation className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Destinație</p>
                    <p className="font-medium">{selectedShipment.destination}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Detalii Colet</h3>
                
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Greutate</p>
                    <p className="font-medium">{selectedShipment.weight} kg</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Package className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Dimensiuni</p>
                    <p className="font-medium">{selectedShipment.dimensions}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Valoare</p>
                    <p className="font-medium">€{selectedShipment.value.toLocaleString()}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Livrare estimată</p>
                    <p className="font-medium">{new Date(selectedShipment.estimatedDelivery).toLocaleString('ro-RO')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Note</h3>
              <p className="text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {selectedShipment.notes}
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setShowShipmentModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Închide
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors">
                <Eye className="w-4 h-4 inline mr-2" />
                Tracking Live
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal adăugare expediere */}
      {showAddShipmentModal && (
        <Modal
          isOpen={showAddShipmentModal}
          onClose={() => setShowAddShipmentModal(false)}
          title="Expediere Nouă"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ID Comandă
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="ORDER-2024-XXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Nume client"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setShowAddShipmentModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Anulează
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors">
                Creează Expediere
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default IntegrareLogistica;